import { FunctionComponent, useState, useEffect } from "react";
import {
  Button,
  LoadingSpinner,
  PageContainer,
  PageTitle,
} from "src/components/generic";
import InviteClientModal from "src/components/generic/Modal/InviteClientModal";
import { TableCheckbox } from "src/components/inputs";
import Table from "src/components/tables/Table";
import { useAuth } from "src/context/AuthContext";
import { ToastStatus, useToast } from "src/context/ToastContext";
import { dynamo } from "src/utils";
import { userTypes } from "src/utils/types";
import { Client } from "src/utils/types/UserTypes";

const Clients: FunctionComponent = () => {
  const columns: string[] = [
    "",
    "ID",
    "Name",
    "Email",
    "Phone",
    "City",
    "State",
    "Lead",
    "Joined",
  ];

  const { user } = useAuth();
  const { setToast } = useToast();

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [allClients, setAllClients] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);

  const toggleModal = () => setOpen(!open);

  const toggleSelectedClients = (checked: boolean, email: string) =>
    checked
      ? setSelectedClients([...selectedClients, email])
      : setSelectedClients(
          selectedClients.filter(
            (selectedEmail: string) => selectedEmail !== email
          )
        );

  const checkValidation = (email: string) => selectedClients.includes(email);

  const rows = clients.map((client: Client) => [
    <TableCheckbox
      checked={checkValidation(client.userInfo.email)}
      data={client.userInfo.email}
      toggleSelected={toggleSelectedClients}
    />,
    client.userId,
    client.userInfo.name,
    client.userInfo.email,
    client.userInfo.phone,
    client.userInfo.city,
    client.userInfo.state,
    client.leadAssigned?.name || "N/A",
    client.metadata.createdAt,
  ]);

  const fetchClients = async () => {
    setLoading(true);
    if (!user) return;

    try {
      const result = await dynamo.fetch.usersByType(
        userTypes.AccountTypes.CLIENT
      );

      setClients(result?.users);
    } catch (error: any) {
      setToast({
        message: error?.message || "Failed to Fetch Clients",
        status: ToastStatus.FAIL,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClientsByConsultant = async () => {
    setLoading(true);
    if (!user) return;

    try {
      const result = await dynamo.fetch.clientsByConsultants(user?.userId);

      setClients(result?.clients);
    } catch (error: any) {
      setToast({
        message: error?.message || "Failed to Fetch Your Clients",
        status: ToastStatus.FAIL,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyClients = async () => {
    setLoading(true);
    if (!user) return;
    console.log('By Company...')
    try {
      const result = await dynamo.fetch.clientsByCompany(user?.userId);

      setClients(result?.clients);
    } catch (error: any) {
      setToast({
        message: error?.message || "Failed to Fetch Your Clients",
        status: ToastStatus.FAIL,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMemberClients = async () => {
    setLoading(true);
    if (!user) return;
    console.log('By Member...')
    try {
      const result = await dynamo.fetch.clientsByCompany(user?.userId);

      setClients(result?.clients);
    } catch (error: any) {
      setToast({
        message: error?.message || "Failed to Fetch Your Clients",
        status: ToastStatus.FAIL,
      });
    } finally {
      setLoading(false);
    }
  };

  const clientvitals =
    user?.userType === userTypes.AccountTypes.NEATT ||
    user?.userType === userTypes.AccountTypes.MANAGER ||
    user?.userType === userTypes.AccountTypes.OWNER;

  const company = user?.userType === userTypes.AccountTypes.COMPANY;

  const showToggle = clientvitals;

  useEffect(() => {
    if (user) {
      if (clientvitals)
        allClients ? fetchClients() : fetchClientsByConsultant();
      else if (user?.userType === userTypes.AccountTypes.REFERRAL)
        fetchClientsByConsultant();
      else company ? fetchCompanyClients() : fetchMemberClients();
    }
  }, [user, allClients]);

  if (loading) return <LoadingSpinner />;
  return (
    <PageContainer>
      <PageTitle title="Clients" />
      <div className="w-full flex justify-between items-center mb-3">
        {showToggle && (
          <Button
            text={allClients ? "Show My Clients" : "Show All Clients"}
            onClick={() => setAllClients(!allClients)}
            styles="w-[200px]"
          />
        )}

        <InviteClientModal open={open} toggleModal={toggleModal} />
      </div>

      <Table columns={columns} rows={rows} />
    </PageContainer>
  );
};

export default Clients;
